/**
 * Everything a player owns, in one place, keyed to their account.
 *
 * ── The problem this fixes ──────────────────────────────────────────────────
 *
 * State used to live in five separate `localStorage` keys with no owner:
 * `interspacial:ship`, `:hull`, `:wallet`, `:cargo`. Two accounts on the same
 * browser shared all of it, and the game had no idea whose ship it was showing.
 * That is fine for one developer testing and wrong the moment a second person
 * signs in on the same machine.
 *
 * Now there is one record per account, under `interspacial:player:<uuid>`, and
 * the game cannot read it without knowing who is asking.
 *
 * ── The shape is the API's shape ────────────────────────────────────────────
 *
 * This is deliberately one document rather than a spread of keys, because that
 * is what the `players` row will be: a uuid and a blob of game state. When the
 * economy API exists, `load` and `save` become `GET` and `PUT` against the same
 * object and nothing above this file changes.
 *
 * `version` is the hinge that makes that survivable. A stored record from an
 * older build is migrated on read rather than discarded — losing somebody's
 * ship because a field moved is not a trade worth making.
 */

import {
  STARTING_SYSTEM_ID,
  shelfEra,
  starterDesign,
  type CargoItem,
  type ShipGraph,
  type SystemId,
  type TutorialStep,
  type Wallet,
} from '@interspacial/shared';

import type { PlayerRecord } from '@interspacial/shared';

import type { FlightState } from '../flightState';

/** Bumped when the shape changes in a way `migrate` has to know about. */
export const PLAYER_STATE_VERSION = 1;

/**
 * What a new account has in hand. Nothing.
 *
 * The sixty linelits that used to be here are now what the introduction's
 * delivery pays when it is handed in — enough to fit out the starter a little,
 * and earned by the flight that teaches you to fly. See `tutorialDelivery`.
 */
export const STARTING_LINELITS = 0;

export interface PlayerState {
  version: number;

  /**
   * Whether this account has ever played.
   *
   * The one flag the tutorial turns on. False means a first-time player: they
   * get the introduction, and their start position is chosen rather than
   * inherited. See `new-player-flow.md`.
   */
  onboarded: boolean;

  /**
   * How far through the introduction they are.
   *
   * Null when the tutorial is not running — either never started, or finished.
   * Kept beside `onboarded` rather than derived from it because a player can
   * replay it, and "has played before" and "is being taught right now" are
   * different questions.
   */
  tutorial: TutorialStep | null;

  /**
   * Which first-time lessons have been shown, and whether the loud half is on.
   *
   * Both are the server's. They are held here so the menu can draw the switch
   * and the flight view can avoid asking about a lesson it already knows was
   * taught — but the decision belongs to `/lesson`, which is the only thing that
   * can be right about it when two tabs are open.
   */
  lessons?: string[];
  tips?: boolean;
  /**
   * Which of a station's counters are open. See `stationPanels.ts`.
   *
   * Server-owned like the two above: a new account lands with the mission board
   * and the rest appear as they become worth showing. Empty until the first
   * `/state` answers, which is a berth with one counter rather than none — the
   * board is always open.
   */
  unlocked?: string[];

  /** When the last free repair was taken, in epoch ms. The server owns it. */
  lastFreeRepair?: number | null;

  /** Which galaxy. One seed for everyone until there is a reason not to be. */
  seed: string;
  systemId: SystemId;

  /** The berth to return to. Null before the first dock. */
  berth: { stationId: string; padId: string } | null;

  /** The design, every line whole. What the editor draws and a rebuy builds. */
  blueprint: ShipGraph;
  /** The hull in service, dents and all. Null when nothing is commissioned. */
  hull: ShipGraph | null;

  wallet: Wallet;
  cargo: CargoItem[];

  /** Where the ship is, if it is out. Null when docked. */
  flight: FlightState | null;

  /**
   * What has been bought off station shelves, and when.
   *
   * Temporary by construction: `era` is which restocking period the counts
   * belong to, so a rollover throws them away without anything having to run on
   * a timer. Keyed `stationId:itemId`.
   *
   * Stored rather than held in the shop panel because a shelf that refilled the
   * moment you undocked made the quantity beside each row a decoration. See
   * `shelfEra`.
   */
  shelves: { era: number; taken: Record<string, number> };
}

/** A brand new account. */
export function freshPlayerState(seed = 'interspacial-dev'): PlayerState {
  return {
    version: PLAYER_STATE_VERSION,
    onboarded: false,
    tutorial: null,
    lastFreeRepair: null,
    seed,
    systemId: STARTING_SYSTEM_ID,
    berth: null,
    // A design to own from the first moment. An empty graph is not a ship, and
    // a player looking at one has nothing to do.
    blueprint: starterDesign(),
    hull: null,
    wallet: { carried: STARTING_LINELITS, banked: 0 },
    cargo: [],
    flight: null,
    shelves: { era: shelfEra(), taken: {} },
  };
}

const key = (uuid: string) => `interspacial:player:${uuid}`;

/**
 * Read this account's state, or start a new one.
 *
 * Never throws. Storage can be disabled, full, or hold something written by a
 * build that no longer exists, and none of those are reasons to refuse to start
 * the game.
 */
export function loadPlayerState(uuid: string): PlayerState {
  try {
    const raw = window.localStorage.getItem(key(uuid));
    if (!raw) return adoptLegacyState() ?? freshPlayerState();

    const parsed: unknown = JSON.parse(raw);
    return migrate(parsed) ?? freshPlayerState();
  } catch {
    return freshPlayerState();
  }
}

export function savePlayerState(uuid: string, state: PlayerState): void {
  try {
    window.localStorage.setItem(key(uuid), JSON.stringify(state));
  } catch {
    // Private browsing, or a full quota. The session still plays; it just will
    // not survive a refresh, and refusing to run is a worse answer.
  }
}

/**
 * Wipe an account back to new.
 *
 * Takes the unowned legacy keys with it. Without that, resetting on a browser
 * that still held them would immediately re-adopt the old ship on the next read
 * — an account that had plainly just been wiped would come back onboarded, with
 * its old hull, and no tutorial. A wipe has to mean a wipe.
 */
export function clearPlayerState(uuid: string): void {
  try {
    window.localStorage.removeItem(key(uuid));
    for (const stale of ['ship', 'hull', 'wallet', 'cargo']) {
      window.localStorage.removeItem(`interspacial:${stale}`);
    }
  } catch {
    // As above.
  }
}

/**
 * Bring a stored record up to date.
 *
 * Field-by-field rather than trusting the shape: this is the boundary between
 * the game and bytes it did not write this session, and a half-valid graph
 * breaks the compartment tracer a long way from here. Returns null when there
 * is nothing recoverable.
 */
function migrate(parsed: unknown): PlayerState | null {
  if (typeof parsed !== 'object' || parsed === null) return null;

  const stored = parsed as Partial<PlayerState>;
  const fresh = freshPlayerState();

  const blueprint = graphOrNull(stored.blueprint);
  if (!blueprint) return null;

  return {
    version: PLAYER_STATE_VERSION,
    onboarded: stored.onboarded === true,
    tutorial: (stored.tutorial as TutorialStep | null) ?? null,
    seed: typeof stored.seed === 'string' && stored.seed ? stored.seed : fresh.seed,
    systemId: (typeof stored.systemId === 'string' ? stored.systemId : fresh.systemId) as SystemId,
    berth:
      stored.berth && typeof stored.berth.stationId === 'string'
        ? { stationId: stored.berth.stationId, padId: String(stored.berth.padId ?? '') }
        : null,
    blueprint,
    hull: graphOrNull(stored.hull),
    wallet: {
      carried: finite(stored.wallet?.carried, fresh.wallet.carried),
      banked: finite(stored.wallet?.banked, 0),
    },
    // Stacks of nothing weigh nothing and cannot be spent, so they are only
    // ever confusing.
    cargo: Array.isArray(stored.cargo)
      ? stored.cargo.filter(
          (item): item is CargoItem =>
            typeof item === 'object' &&
            item !== null &&
            typeof (item as CargoItem).id === 'string' &&
            typeof (item as CargoItem).count === 'number' &&
            (item as CargoItem).count > 0,
        )
      : [],
    flight: (stored.flight as FlightState | null) ?? null,
    // Dropped outright when it belongs to a period that has passed — which is
    // the whole mechanism, and it costs nothing to do it on load as well.
    shelves:
      stored.shelves && stored.shelves.era === shelfEra() && typeof stored.shelves.taken === 'object'
        ? { era: stored.shelves.era, taken: { ...stored.shelves.taken } }
        : { era: shelfEra(), taken: {} },
  };
}

function finite(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : fallback;
}

function graphOrNull(value: unknown): ShipGraph | null {
  if (typeof value !== 'object' || value === null) return null;

  const graph = value as Partial<ShipGraph>;
  if (!Array.isArray(graph.joints) || !Array.isArray(graph.lines) || !Array.isArray(graph.components)) {
    return null;
  }
  return { joints: graph.joints, lines: graph.lines, components: graph.components };
}

// ─── The old unowned keys ────────────────────────────────────────────────────

/**
 * Adopt whatever was lying around before state had an owner.
 *
 * There is exactly one person with a ship in these keys and it is the developer
 * who has been testing all of this, so throwing it away to be tidy would be a
 * poor trade. Runs once: the keys are removed as they are taken, so a second
 * account signing in on the same browser starts clean rather than inheriting
 * the first one's ship.
 */
function adoptLegacyState(): PlayerState | null {
  try {
    const design = window.localStorage.getItem('interspacial:ship');
    if (!design) return null;

    const state = freshPlayerState();
    const blueprint = graphOrNull(JSON.parse(design)?.graph);
    if (blueprint && blueprint.lines.length > 0) state.blueprint = blueprint;

    const hull = window.localStorage.getItem('interspacial:hull');
    if (hull) state.hull = graphOrNull(JSON.parse(hull)?.graph);

    const wallet = window.localStorage.getItem('interspacial:wallet');
    if (wallet) {
      const held = JSON.parse(wallet) as Partial<Wallet>;
      state.wallet = {
        carried: finite(held.carried, STARTING_LINELITS),
        banked: finite(held.banked, 0),
      };
    }

    const cargo = window.localStorage.getItem('interspacial:cargo');
    if (cargo) {
      const held: unknown = JSON.parse(cargo);
      if (Array.isArray(held)) state.cargo = held as CargoItem[];
    }

    // Anyone with a ship in the old keys has plainly played.
    state.onboarded = true;

    for (const stale of ['ship', 'hull', 'wallet', 'cargo']) {
      window.localStorage.removeItem(`interspacial:${stale}`);
    }

    return state;
  } catch {
    return null;
  }
}

// ── The account, as against the session ────────────────────────────────────

/**
 * What the server owns, taken out of the session record.
 *
 * `flight` and `shelves` are deliberately absent. Position is not persisted at
 * all — a returning player is always docked — and shelf depletion is shared
 * between players now, so it is world state rather than something an account
 * carries. Both stay client-side for the moment; shelves move server-side with
 * the buy endpoint.
 */
export function toRecord(
  state: PlayerState,
  identity: { uuid: string; displayID: string; username: string },
): PlayerRecord {
  return {
    uuid: identity.uuid,
    displayId: identity.displayID,
    username: identity.username,
    /*
     * Present and ignored, like the repair cooldown below.
     *
     * `PUT /state` rebuilds the record from what is stored and never reads
     * these, so a client cannot teach itself a lesson or untick the switch by
     * saving. They are here to satisfy the shape, not to be believed.
     */
    lessons: state.lessons ?? [],
    tips: state.tips ?? true,
    unlocked: state.unlocked ?? [],
    /*
     * Sent back as the server last said it.
     *
     * The cooldown is the server's to set — `PUT /state` ignores it, like
     * everything else a client could otherwise grant itself — so this only has
     * to be present and truthful, not authoritative.
     */
    lastFreeRepair: state.lastFreeRepair ?? null,
    onboarded: state.onboarded,
    tutorial: state.tutorial,
    seed: state.seed,
    systemId: state.systemId,
    berth: state.berth,
    blueprint: state.blueprint,
    hull: state.hull,
    wallet: state.wallet,
    cargo: state.cargo,
    // Neither of these is owned here. The server reads its own copies and
    // ignores what arrives, so these are shape rather than content.
    factions: {} as PlayerRecord['factions'],
    shelves: {},
    era: shelfEra(),
  };
}

/**
 * The session record, from what the server holds.
 *
 * The fields the server does not keep are started fresh rather than carried over
 * from whatever was on screen: a load is a load, and inventing a position for a
 * ship the server believes is docked is exactly the confusion this avoids.
 */
export function fromRecord(record: PlayerRecord): PlayerState {
  return {
    version: PLAYER_STATE_VERSION,
    onboarded: record.onboarded,
    lessons: record.lessons ?? [],
    tips: record.tips ?? true,
    unlocked: record.unlocked ?? [],
    tutorial: record.tutorial,
    lastFreeRepair: record.lastFreeRepair,
    seed: record.seed,
    systemId: record.systemId,
    berth: record.berth,
    blueprint: record.blueprint,
    hull: record.hull,
    wallet: record.wallet,
    cargo: record.cargo,
    flight: null,
    // The server's era, not the browser's — its clock decides what a station
    // has on the shelf, and a browser with a wrong clock would be shown a
    // different shop from the one the server will honour. `taken` is the shared
    // count for the station being stood in, not this account's own tally.
    shelves: { era: record.era, taken: record.shelves ?? {} },
  };
}
